import {type MediaCardType} from '@wandb/weave/common/types/media';
import {type Selector} from 'reselect';

import type * as PanelTypes from '../../../../state/views/panel/types';
import type * as PanelBankSectionConfigTypes from '../../../../state/views/panelBankSectionConfig/types';
import {type Ref as SectionRef} from '../../../../state/views/panelBankSectionConfig/types';
import {type Ref as WorkspaceSettingsRef} from '../../../../state/views/workspaceSettings/types';
import {type RootState} from '../../../../types/redux';
import {type ClassLabelMap} from '../../../../util/media';
import {type RampKey} from '../../../../util/useRampFlag';
import {type MediaBrowserPanelConfig} from '../../../PanelMediaBrowser/common';
import {type MediaConfigWithDefaults} from '../../../PanelMediaBrowser/Component';

export enum SettingsLevel {
  PANEL = 'panel',
  SECTION = 'section',
  WORKSPACE = 'workspace',
}

export type MediaSettingsComponentKeySelectorFactory = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref,
  isSingleRun?: boolean
) => Selector<RootState, string>;

export type MediaSettingsShowOverridesSelectorFactory = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) => Selector<RootState, boolean>;

export type MediaSettingsShouldShowSelectorFactory = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) => Selector<RootState, boolean>;

export type MediaSetting = {
  component: React.FC<{}>;
  mediaTypes: MediaCardType[]; // The media types that this setting is relevant for
  /** Static (non-Redux) visibility conditions, e.g. media metadata or ramp flags. */
  shouldShow?: (
    hasMasks: boolean,
    hasBoundingBoxes: boolean,
    passingRampFlags?: Partial<Record<RampKey, boolean>>
  ) => boolean;
  /**
   * Selector factory for config-dependent visibility, evaluated at render
   * time so drawer filtering subscribes to specific Redux fields instead of
   * the monolithic context `config`.
   */
  shouldShowSelector?: MediaSettingsShouldShowSelectorFactory;
  /**
   * @deprecated Prefer `getComponentKeySelector` so remount keys subscribe to
   * specific Redux fields instead of the monolithic context `config`.
   */
  getComponentKey?: (config: MediaConfigWithDefaults) => string;
  /** Selector factory for component remount keys; preferred over `getComponentKey`. */
  getComponentKeySelector?: MediaSettingsComponentKeySelectorFactory;
  /**
   * Selector factory for whether to show override clear rows for this setting.
   * Preferred over reading `config[configKey]` from context.
   */
  showOverridesSelector?: MediaSettingsShowOverridesSelectorFactory;
  /**
   * UI-only restriction on which drawer levels show this control. When omitted,
   * levels are read from `mediaSettings[configKeys[0]].levels`.
   */
  settingsLevels?: SettingsLevel[];
  configKeys: (keyof MediaBrowserPanelConfig)[];
  subsettings?: MediaSetting[];
};

export type MediaSettingsSection = {
  title: string;
  settings: MediaSetting[];
};

export type MediaSettingsTab = {
  title: string;
  sections: MediaSettingsSection[];
};

export type MediaSettingsDrawerPanelProps = {
  panelRef: PanelTypes.Ref;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
};

/**
 * Actions context - contains stable callbacks and static values.
 * These rarely change and won't cause unnecessary re-renders.
 */
export type MediaSettingsActionsContextValue = {
  updateConfig: (config: Partial<MediaConfigWithDefaults>) => void;
  unsetConfigField: (field: keyof MediaConfigWithDefaults) => void;
  clearOverridesBelow?: Partial<
    Record<SettingsLevel, (key: keyof MediaBrowserPanelConfig) => void>
  >;
  settingsLevel: SettingsLevel;
  /** Set at panel level so settings can read panel values via Redux selectors. */
  panelRef?: PanelTypes.Ref;
  rampFlags: Partial<Record<RampKey, boolean>> | undefined;
};

/**
 * State context - contains dynamic values that change during interaction/loading.
 * Components that only need actions won't re-render when state changes.
 */
export type MediaSettingsStateContextValue = {
  config: MediaConfigWithDefaults;
  configWithoutDefaults: MediaBrowserPanelConfig;
  hasMasks: boolean;
  hasBoundingBoxes: boolean;
  loading: boolean;
  mediaCardTypes: MediaCardType[] | undefined;
  allBoxes: ClassLabelMap;
  sliderRanges: Record<
    string,
    {
      min: number;
      max: number;
    }
  > | null;
  allMasks: ClassLabelMap;
  numberOfOverridesBelow?: Partial<
    Record<
      SettingsLevel,
      Partial<Record<keyof MediaBrowserPanelConfig, number>>
    >
  >;
};

export type ContextProviderState = MediaSettingsActionsContextValue &
  MediaSettingsStateContextValue;
